import { useState, useRef, useEffect } from "react";
import { api, eur } from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MAR_ACCOUNTS, marName } from "@/lib/mar";
import { Landmark, UploadCloud, CheckCircle2, Copy, ArrowRight, Loader2, Percent, FileSpreadsheet, ListChecks, Trash2, Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";

const MONTH_NL = { "01": "januari", "02": "februari", "03": "maart", "04": "april", "05": "mei", "06": "juni", "07": "juli", "08": "augustus", "09": "september", "10": "oktober", "11": "november", "12": "december" };
const monthLabel = (m) => { const [y, mm] = (m || "").split("-"); return `${MONTH_NL[mm] || mm} ${y}`; };

export default function BankProviders() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [bnpBusy, setBnpBusy] = useState(false);
  const [bnpResult, setBnpResult] = useState(null);
  const [recBusy, setRecBusy] = useState(false);
  const [loanBusy, setLoanBusy] = useState(false);
  const [loanResult, setLoanResult] = useState(null);
  const [loans, setLoans] = useState([]);
  const loadLoans = () => { api.get("/loans").then((r) => setLoans(r.data.loans)).catch(() => setLoans([])); };
  const fileRef = useRef(null);
  const bnpRef = useRef(null);
  const loanRef = useRef(null);
  const navigate = useNavigate();

  const [bnpCats, setBnpCats] = useState([]);
  const [bnpTab, setBnpTab] = useState("pending");
  const [bnpTxs, setBnpTxs] = useState([]);
  const [bnpCounts, setBnpCounts] = useState({ pending_count: 0, confirmed_count: 0 });
  const [bnpShow, setBnpShow] = useState(30);
  const [txBusy, setTxBusy] = useState("");
  const [bnpSearch, setBnpSearch] = useState("");
  const [sel, setSel] = useState(new Set());
  const [bulkActBusy, setBulkActBusy] = useState(false);

  const [vivaTxs, setVivaTxs] = useState([]);
  const [vivaCounts, setVivaCounts] = useState({ pending_count: 0, confirmed_count: 0 });
  const loadVivaTxs = () => {
    api.get("/providers/viva-statement/transactions")
      .then((r) => { setVivaTxs(r.data.transactions); setVivaCounts({ pending_count: r.data.pending_count, confirmed_count: r.data.confirmed_count }); })
      .catch(() => setVivaTxs([]));
  };
  useEffect(() => { loadVivaTxs(); loadLoans(); }, []);

  const [vivaMatch, setVivaMatch] = useState({}); // entryId -> {candidates, entry}
  const [vivaMatchBusy, setVivaMatchBusy] = useState("");
  const loadVivaMatch = async (id) => {
    if (vivaMatch[id]) { setVivaMatch((m) => { const n = { ...m }; delete n[id]; return n; }); return; }
    setVivaMatchBusy(id);
    try {
      const r = await api.get(`/providers/viva/transactions/${id}/match-invoices`);
      setVivaMatch((m) => ({ ...m, [id]: r.data }));
    } catch (e) { toast.error("Zoeken mislukt"); }
    finally { setVivaMatchBusy(""); }
  };
  const linkVivaInvoice = async (id, invId) => {
    setVivaMatchBusy(id);
    try {
      const r = await api.post(`/providers/viva/transactions/${id}/link`, { invoice_id: invId });
      toast.success(`Gekoppeld aan factuur ${r.data.invoice_number}${r.data.paid ? " · volledig betaald" : " · deels betaald"}`);
      setVivaMatch((m) => { const n = { ...m }; delete n[id]; return n; });
      loadVivaTxs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Koppelen mislukt"); }
    finally { setVivaMatchBusy(""); }
  };
  const unlinkVivaInvoice = async (id) => {
    setVivaMatchBusy(id);
    try {
      await api.post(`/providers/viva/transactions/${id}/unlink`);
      toast.success("Ontkoppeld");
      loadVivaTxs();
    } catch (e) { toast.error("Ontkoppelen mislukt"); }
    finally { setVivaMatchBusy(""); }
  };

  // Viva-transacties bevestigen — zelfde werkwijze als BNP
  const [vivaTab, setVivaTab] = useState("pending");
  const [vivaSearch, setVivaSearch] = useState("");
  const [vivaSel, setVivaSel] = useState(new Set());
  const [vivaShow, setVivaShow] = useState(30);
  const [vivaTxBusy, setVivaTxBusy] = useState("");
  const [vivaBulkBusy, setVivaBulkBusy] = useState(false);
  const [vivaLineEdit, setVivaLineEdit] = useState({});
  useEffect(() => { setVivaShow(30); setVivaSel(new Set()); }, [vivaTab]);

  const vivaFiltered = vivaTxs.filter((t) => (t.status === "confirmed") === (vivaTab === "confirmed"));
  const visibleVivaTxs = vivaFiltered.filter((t) => {
    const q = vivaSearch.trim().toLowerCase();
    if (!q) return true;
    const hay = `${t.description || ""} ${t.mededeling || ""} ${t.category_label || ""} ${t.date || ""} ${t.amount || ""} ${t.linked_invoice_number || ""} ${(t.lines || []).map((l) => `${l.account} ${l.name}`).join(" ")}`.toLowerCase();
    return hay.includes(q);
  });
  const vivaShownIds = visibleVivaTxs.slice(0, vivaShow).map((t) => t.id);
  const vivaAllShownSelected = vivaShownIds.length > 0 && vivaShownIds.every((id) => vivaSel.has(id));
  const toggleVivaSel = (id) => setVivaSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleVivaAll = () => setVivaSel((s) => { const n = new Set(s); if (vivaAllShownSelected) vivaShownIds.forEach((id) => n.delete(id)); else vivaShownIds.forEach((id) => n.add(id)); return n; });
  const confirmVivaTx = async (id) => {
    setVivaTxBusy(id);
    try {
      await api.post(`/ledger/entries/${id}/confirm`);
      toast.success("Transactie bevestigd");
      loadVivaTxs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bevestigen mislukt"); }
    finally { setVivaTxBusy(""); }
  };
  const bulkConfirmVivaSel = async () => {
    setVivaBulkBusy(true);
    try {
      const r = await api.post("/providers/viva/bulk-confirm", { ids: [...vivaSel] });
      toast.success(`${r.data.confirmed} bevestigd${r.data.skipped ? ` · ${r.data.skipped} overgeslagen (niet in balans)` : ""}`);
      setVivaSel(new Set()); loadVivaTxs();
    } catch (e) { toast.error("Bevestigen mislukt"); }
    finally { setVivaBulkBusy(false); }
  };
  const bulkDeleteVivaSel = async () => {
    if (!window.confirm(`${vivaSel.size} geselecteerde Viva-transactie(s) verwijderen? Je kan ze terugkrijgen door het Viva-statement opnieuw te uploaden.`)) return;
    setVivaBulkBusy(true);
    try {
      const r = await api.post("/providers/viva/bulk-delete", { ids: [...vivaSel] });
      toast.success(`${r.data.deleted} verwijderd${r.data.skipped ? ` · ${r.data.skipped} overgeslagen (gekoppeld)` : ""}`);
      setVivaSel(new Set()); loadVivaTxs();
    } catch (e) { toast.error("Verwijderen mislukt"); }
    finally { setVivaBulkBusy(false); }
  };
  const recomputeViva = async () => {
    setVivaBulkBusy(true);
    try {
      const r = await api.post("/providers/viva/recompute-accounts");
      toast.success(`${r.data.updated} Viva-boeking(en) bijgewerkt (58 + kostenrekeningen 657)`);
      loadVivaTxs();
    } catch (e) { toast.error("Herberekenen mislukt"); }
    finally { setVivaBulkBusy(false); }
  };
  const startVivaLineEdit = (t) => setVivaLineEdit((m) => ({ ...m, [t.id]: t.lines.map((l) => ({ ...l })) }));
  const cancelVivaLineEdit = (id) => setVivaLineEdit((m) => { const n = { ...m }; delete n[id]; return n; });
  const setVivaLine = (id, i, field, val) => setVivaLineEdit((m) => {
    const rows = m[id].map((l) => ({ ...l }));
    rows[i][field] = val;
    if (field === "account") { const nm = marName(val); if (nm) rows[i].name = nm; }
    if (field === "debet" && (parseFloat(val) || 0) !== 0) rows[i].credit = 0;
    if (field === "credit" && (parseFloat(val) || 0) !== 0) rows[i].debet = 0;
    return { ...m, [id]: rows };
  });
  const addVivaLine = (id) => setVivaLineEdit((m) => ({ ...m, [id]: [...m[id], { account: "", name: "", debet: 0, credit: 0 }] }));
  const removeVivaLine = (id, i) => setVivaLineEdit((m) => ({ ...m, [id]: m[id].filter((_, idx) => idx !== i) }));
  const saveVivaLines = async (id) => {
    const rows = (vivaLineEdit[id] || []).map((l) => ({ account: (l.account || "").toString().trim(), name: (l.name || "").toString().trim(), debet: parseFloat(l.debet) || 0, credit: parseFloat(l.credit) || 0 }));
    try {
      const r = await api.patch(`/providers/viva/transactions/${id}/lines`, { lines: rows });
      setVivaTxs((xs) => xs.map((x) => (x.id === id ? { ...x, lines: r.data.lines, total_debet: r.data.total_debet, amount: Math.abs(r.data.total_debet) } : x)));
      cancelVivaLineEdit(id);
      toast.success("Boeking aangepast");
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
  };

  const loadBnpTxs = (tab = bnpTab) => {
    api.get("/providers/bnp/transactions", { params: { status: tab } })
      .then((r) => { setBnpTxs(r.data.transactions); setBnpCounts({ pending_count: r.data.pending_count, confirmed_count: r.data.confirmed_count }); })
      .catch(() => setBnpTxs([]));
  };
  useEffect(() => { api.get("/providers/bnp/categories").then((r) => setBnpCats(r.data.categories)).catch(() => {}); }, []);
  useEffect(() => { setBnpShow(30); setSel(new Set()); loadBnpTxs(bnpTab); /* eslint-disable-next-line */ }, [bnpTab]);

  const visibleTxs = bnpTxs.filter((t) => {
    const q = bnpSearch.trim().toLowerCase();
    if (!q) return true;
    const hay = `${t.description || ""} ${t.volgnummer || ""} ${t.mededeling || ""} ${t.category_label || ""} ${t.date || ""} ${t.amount || ""} ${(t.lines || []).map((l) => `${l.account} ${l.name}`).join(" ")}`.toLowerCase();
    return hay.includes(q);
  });
  const toggleSel = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const shownIds = visibleTxs.slice(0, bnpShow).map((t) => t.id);
  const allShownSelected = shownIds.length > 0 && shownIds.every((id) => sel.has(id));
  const toggleAll = () => setSel((s) => { const n = new Set(s); if (allShownSelected) shownIds.forEach((id) => n.delete(id)); else shownIds.forEach((id) => n.add(id)); return n; });
  const bulkConfirmSel = async () => {
    setBulkActBusy(true);
    try {
      const r = await api.post("/providers/bnp/bulk-confirm", { ids: [...sel] });
      toast.success(`${r.data.confirmed} bevestigd${r.data.skipped ? ` · ${r.data.skipped} overgeslagen (niet in balans of gekoppeld)` : ""}`);
      setSel(new Set()); loadBnpTxs();
    } catch (e) { toast.error("Bevestigen mislukt"); }
    finally { setBulkActBusy(false); }
  };
  const bulkDeleteSel = async () => {
    if (!window.confirm(`${sel.size} geselecteerde transactie(s) definitief verwijderen? Je kan ze terugkrijgen door het BNP-uittreksel opnieuw te uploaden.`)) return;
    setBulkActBusy(true);
    try {
      const r = await api.post("/providers/bnp/bulk-delete", { ids: [...sel] });
      toast.success(`${r.data.deleted} verwijderd${r.data.skipped ? ` · ${r.data.skipped} overgeslagen (gekoppeld)` : ""}`);
      setSel(new Set()); loadBnpTxs();
    } catch (e) { toast.error("Verwijderen mislukt"); }
    finally { setBulkActBusy(false); }
  };

  const setCategory = async (id, category) => {
    setBnpTxs((xs) => xs.map((x) => (x.id === id ? { ...x, category } : x)));
    try {
      const r = await api.patch(`/providers/bnp/transactions/${id}`, { category });
      setBnpTxs((xs) => xs.map((x) => (x.id === id ? { ...x, category: r.data.category, category_label: r.data.category_label, lines: r.data.lines } : x)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Wijzigen mislukt"); loadBnpTxs(); }
  };
  // Bulk afpunten: alle exacte BNP↔factuur-matches
  const [bulkPairs, setBulkPairs] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const findBulk = async () => {
    setBulkBusy(true);
    try {
      const r = await api.get("/providers/bnp/bulk-match");
      setBulkPairs(r.data.pairs);
      if (r.data.count === 0) toast.info("Geen exacte matches gevonden");
    } catch (e) { toast.error("Zoeken mislukt"); }
    finally { setBulkBusy(false); }
  };
  const linkBulk = async () => {
    setBulkBusy(true);
    try {
      const r = await api.post("/providers/bnp/bulk-link", { pairs: bulkPairs.map((p) => ({ bnp_id: p.bnp_id, invoice_id: p.invoice_id })) });
      toast.success(`${r.data.linked} betalingen gekoppeld${r.data.errors ? ` · ${r.data.errors} overgeslagen` : ""}`);
      setBulkPairs(null); loadBnpTxs();
    } catch (e) { toast.error("Koppelen mislukt"); }
    finally { setBulkBusy(false); }
  };
  // Automatisch koppelen wanneer factuurnummer = mededeling van de betaling
  const [autoBusy, setAutoBusy] = useState(false);
  const autoLink = async () => {
    setAutoBusy(true);
    try {
      const r = await api.post("/providers/bnp/auto-link");
      if (r.data.linked > 0) {
        toast.success(`${r.data.linked} betaling(en) automatisch gekoppeld — klik nu op "Bevestigen".${r.data.skipped_ambiguous ? ` (${r.data.skipped_ambiguous} met meerdere mogelijke facturen overgeslagen)` : ""}`);
      } else {
        toast.info(`Geen betalingen gevonden waarvan het factuurnummer in de mededeling staat.${r.data.skipped_ambiguous ? ` (${r.data.skipped_ambiguous} dubbelzinnig)` : ""}`);
      }
      loadBnpTxs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Automatisch koppelen mislukt"); }
    finally { setAutoBusy(false); }
  };

  const confirmTx = async (id) => {
    setTxBusy(id);
    try {
      await api.post(`/ledger/entries/${id}/confirm`);
      toast.success("Transactie bevestigd");
      loadBnpTxs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bevestigen mislukt"); }
    finally { setTxBusy(""); }
  };

  // BNP-betaling koppelen aan een aankoopfactuur (zoals Viva)
  const [bnpMatch, setBnpMatch] = useState({}); // id -> {candidates, entry}
  const [bnpMatchBusy, setBnpMatchBusy] = useState("");
  const loadBnpMatch = async (id) => {
    if (bnpMatch[id]) { setBnpMatch((m) => { const n = { ...m }; delete n[id]; return n; }); return; }
    setBnpMatchBusy(id);
    try {
      const r = await api.get(`/providers/bnp/transactions/${id}/match-invoices`);
      setBnpMatch((m) => ({ ...m, [id]: r.data }));
    } catch (e) { toast.error("Kandidaten laden mislukt"); }
    finally { setBnpMatchBusy(""); }
  };
  const linkBnpInvoice = async (id, invId) => {
    setBnpMatchBusy(id);
    try {
      const r = await api.post(`/providers/bnp/transactions/${id}/link`, { invoice_id: invId });
      toast.success(`Gekoppeld aan factuur ${r.data.invoice_number}${r.data.confirmed ? (r.data.paid ? " · volledig betaald" : " · deels betaald") : " · bevestig de betaling om ze mee te tellen"}`);
      setBnpMatch((m) => { const n = { ...m }; delete n[id]; return n; });
      loadBnpTxs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Koppelen mislukt"); }
    finally { setBnpMatchBusy(""); }
  };
  const unlinkBnpInvoice = async (id) => {
    setBnpMatchBusy(id);
    try {
      await api.post(`/providers/bnp/transactions/${id}/unlink`);
      toast.success("Ontkoppeld");
      loadBnpTxs();
    } catch (e) { toast.error("Ontkoppelen mislukt"); }
    finally { setBnpMatchBusy(""); }
  };

  // MAR-regels van een BNP-transactie handmatig aanpassen
  const [bnpLineEdit, setBnpLineEdit] = useState({}); // id -> [lines]
  const startLineEdit = (t) => setBnpLineEdit((m) => ({ ...m, [t.id]: t.lines.map((l) => ({ ...l })) }));
  const cancelLineEdit = (id) => setBnpLineEdit((m) => { const n = { ...m }; delete n[id]; return n; });
  const setLine = (id, i, field, val) => setBnpLineEdit((m) => {
    const rows = m[id].map((l) => ({ ...l }));
    rows[i][field] = val;
    if (field === "account") { const nm = marName(val); if (nm) rows[i].name = nm; }
    if (field === "debet" && (parseFloat(val) || 0) !== 0) rows[i].credit = 0;
    if (field === "credit" && (parseFloat(val) || 0) !== 0) rows[i].debet = 0;
    return { ...m, [id]: rows };
  });
  const addLine = (id) => setBnpLineEdit((m) => ({ ...m, [id]: [...m[id], { account: "", name: "", debet: 0, credit: 0 }] }));
  const removeLine = (id, i) => setBnpLineEdit((m) => ({ ...m, [id]: m[id].filter((_, idx) => idx !== i) }));
  const saveLines = async (id) => {
    const rows = (bnpLineEdit[id] || []).map((l) => ({ account: (l.account || "").toString().trim(), name: (l.name || "").toString().trim(), debet: parseFloat(l.debet) || 0, credit: parseFloat(l.credit) || 0 }));
    try {
      const r = await api.patch(`/providers/bnp/transactions/${id}/lines`, { lines: rows });
      setBnpTxs((xs) => xs.map((x) => (x.id === id ? { ...x, lines: r.data.lines, total_debet: r.data.total_debet, total_credit: r.data.total_credit } : x)));
      cancelLineEdit(id);
      toast.success("Boeking aangepast");
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
  };

  const upload = async (files) => {
    const list = files ? [...files] : [];
    if (list.length === 0) return;
    setBusy(true); setResult(null);
    let created = 0, dups = 0, ok = 0, failed = 0;
    try {
      for (const file of list) {
        try {
          const fd = new FormData();
          fd.append("file", file);
          const r = await api.post("/providers/viva-statement/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          created += r.data.bookings_created || 0;
          dups += r.data.duplicates_skipped || 0;
          ok += 1;
          setResult(r.data);
        } catch (e) {
          failed += 1;
          toast.error(`${file.name}: ${e?.response?.data?.detail || "upload mislukt"}`);
        }
      }
      if (ok > 0) toast.success(`${ok} bestand(en) · ${created} boekingsvoorstellen · ${dups} dubbels overgeslagen${failed ? ` · ${failed} mislukt` : ""}`);
      loadVivaTxs();
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const uploadBnp = async (files) => {
    const list = files ? [...files] : [];
    if (list.length === 0) return;
    setBnpBusy(true); setBnpResult(null);
    let txs = 0, splits = 0, dups = 0, ok = 0, failed = 0;
    try {
      for (const file of list) {
        try {
          const fd = new FormData();
          fd.append("file", file);
          const r = await api.post("/providers/bnp/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          txs += r.data.new_transactions || 0;
          splits += r.data.loan_splits || 0;
          dups += r.data.duplicates_skipped || 0;
          ok += 1;
          setBnpResult(r.data);
        } catch (e) {
          failed += 1;
          toast.error(`${file.name}: ${e?.response?.data?.detail || "upload mislukt"}`);
        }
      }
      if (ok > 0) toast.success(`${ok} bestand(en) · ${txs} transacties · ${splits} leningaflossingen gesplitst · ${dups} dubbels${failed ? ` · ${failed} mislukt` : ""}`);
      setBnpTab("pending"); loadBnpTxs("pending");
    } finally { setBnpBusy(false); if (bnpRef.current) bnpRef.current.value = ""; }
  };

  const recomputeCash = async () => {
    setRecBusy(true);
    try {
      const r = await api.post("/providers/bnp/recompute-cash-transfers");
      toast.success(`${r.data.updated} kas↔bank-boeking(en) herberekend via wachtrekening 58`);
      loadBnpTxs(bnpTab);
    } catch (e) { toast.error(e?.response?.data?.detail || "Herberekenen mislukt"); }
    finally { setRecBusy(false); }
  };

  const reclassifyKnown = async () => {
    setRecBusy(true);
    try {
      const r = await api.post("/providers/bnp/reclassify-known");
      toast.success(`${r.data.updated} boeking(en) herbekend (CLB → leverancier, bestuurders 455900/416100)`);
      loadBnpTxs(bnpTab);
    } catch (e) { toast.error(e?.response?.data?.detail || "Herbekennen mislukt"); }
    finally { setRecBusy(false); }
  };

  const uploadLoan = async (file) => {    if (!file) return;
    setLoanBusy(true); setLoanResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/providers/loan/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLoanResult(r.data);
      toast.success(`${r.data.is_update ? "Aflossingstabel bijgewerkt" : "Aflossingstabel toegevoegd"} · contract ${r.data.contract}${r.data.contract_detected ? "" : " (contractnr. niet herkend)"}`);
      loadLoans();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Geen aflossingstabel herkend");
    } finally { setLoanBusy(false); if (loanRef.current) loanRef.current.value = ""; }
  };
  const deleteLoan = async (id, contract) => {
    if (!window.confirm(`Lening ${contract} verwijderen? De automatische kapitaal/rente-splitsing van BNP-aflossingen van deze lening vervalt dan.`)) return;
    try {
      await api.delete(`/loans/${id}`);
      toast.success("Lening verwijderd");
      loadLoans();
    } catch (e) { toast.error("Verwijderen mislukt"); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="providers-page">
      <datalist id="mar-accounts">
        {MAR_ACCOUNTS.map((a) => <option key={a.code} value={a.code} label={`${a.code} · ${a.name}`} />)}
      </datalist>
      <div>
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2"><Landmark className="h-6 w-6 text-primary" /> Bank & Betaalproviders</h1>
        <p className="text-sm text-muted-foreground mt-1">Upload je afschriften (PDF). Het systeem leest ze uit, verwijdert dubbels automatisch en stelt de grootboekboekingen voor.</p>
      </div>

      <Card className="shadow-sm border-primary/20">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <span className="inline-flex items-center justify-center h-7 w-7 rounded-md bg-emerald-600 text-white text-xs font-bold">V</span>
            Viva Wallet — Account Statement
          </CardTitle>
          <p className="text-sm text-muted-foreground">Upload het <b>maandelijkse Viva Account Statement</b> (PDF). Per transactiedatum worden chronologische boekingsvoorstellen aangemaakt (<i>nog te bevestigen</i>): kaartontvangsten (afpunting vordering), transactie-/abonnementskosten, financiële opbrengsten, overboekingen Viva→BNP en Viva→Viva, en kaartbetalingen. Verantwoordingsstuk = <span className="font-mono">Account Statement VIVA mm/jjjj</span>. Dubbels worden automatisch overgeslagen.</p>
        </CardHeader>
        <CardContent>
          <div
            className="border-2 border-dashed rounded-xl p-8 text-center hover:bg-accent/40 transition-colors cursor-pointer"
            data-testid="viva-dropzone"
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
          >
            <input ref={fileRef} type="file" accept="application/pdf" multiple className="hidden" data-testid="viva-file-input"
              onChange={(e) => upload(e.target.files)} />
            {busy ? (
              <div className="flex flex-col items-center gap-2 text-muted-foreground"><Loader2 className="h-8 w-8 animate-spin" /> Statement wordt uitgelezen…</div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <UploadCloud className="h-9 w-9 text-primary" />
                <div className="font-medium">Sleep je Viva Account Statements hierheen of klik om te kiezen</div>
                <div className="text-xs text-muted-foreground">Eén of meerdere PDF's tegelijk · wordt lokaal uitgelezen, geen extra kosten</div>
              </div>
            )}
          </div>

          {result && (
            <div className="mt-5 space-y-4" data-testid="viva-result">
              <div className="flex flex-wrap gap-2 items-center">
                <Badge variant="outline" className="text-primary border-primary/40 gap-1" data-testid="viva-proof">{result.proof}</Badge>
                {result.wallet && <Badge variant="outline" className="text-indigo-700 border-indigo-300 gap-1" data-testid="viva-wallet">{result.wallet.account} · {result.wallet.name}</Badge>}
                <Badge variant="outline" className="text-emerald-700 border-emerald-300 gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> {result.bookings_created} boekingsvoorstellen</Badge>
                <Badge variant="outline" className="text-slate-600 border-slate-300 gap-1">{result.transactions_parsed} verrichtingen gelezen</Badge>
                <Badge variant="outline" className="text-amber-700 border-amber-300 gap-1"><Copy className="h-3.5 w-3.5" /> {result.duplicates_skipped} dubbels overgeslagen</Badge>
              </div>

              {result.breakdown && result.breakdown.length > 0 && (
                <div className="rounded-lg border" data-testid="viva-breakdown">
                  <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground bg-muted/40">Onderverdeling per soort (controle)</div>
                  <div className="divide-y">
                    {result.breakdown.map((b) => (
                      <div key={b.category} className="px-4 py-2 flex items-center justify-between text-sm" data-testid={`viva-bd-${b.category}`}>
                        <span>{b.label} <span className="text-muted-foreground text-xs">· {b.count}×</span></span>
                        <span className={`font-mono ${b.amount < 0 ? "text-red-600" : "text-emerald-700"}`}>{eur(b.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.card_receipts_per_day && result.card_receipts_per_day.length > 0 && (
                <details className="rounded-lg border" data-testid="viva-perday">
                  <summary className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground bg-muted/40 cursor-pointer">Kaartontvangsten per dag ({result.card_receipts_per_day.length} dagen) — vergelijk met je POS/terminal</summary>
                  <div className="divide-y max-h-72 overflow-auto">
                    {result.card_receipts_per_day.map((d) => (
                      <div key={d.date} className="px-4 py-1.5 flex items-center justify-between text-sm" data-testid={`viva-day-${d.date}`}>
                        <span>{d.date}</span><span className="font-mono">{eur(d.amount)}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {result.reconciliation && (
                <div className={`rounded-lg border p-4 ${result.reconciliation.match ? "border-emerald-300 bg-emerald-50/60" : "border-amber-300 bg-amber-50/60"}`} data-testid="viva-reconciliation">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Controle — kaartontvangsten + cash vs. omzet incl. btw ({result.period})</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div><div className="text-muted-foreground text-xs">Kaartontvangsten (Viva)</div><div className="font-semibold" data-testid="viva-card-receipts">{eur(result.reconciliation.card_receipts)}</div></div>
                    <div><div className="text-muted-foreground text-xs">Cash (kasboek)</div><div className="font-semibold" data-testid="viva-cash-receipts">{eur(result.reconciliation.cash_receipts)}</div></div>
                    <div><div className="text-muted-foreground text-xs">Omzet incl. btw</div><div className="font-semibold" data-testid="viva-turnover">{eur(result.reconciliation.turnover_incl_vat)}</div></div>
                    <div><div className="text-muted-foreground text-xs">Verschil</div><div className={`font-bold ${result.reconciliation.match ? "text-emerald-700" : "text-amber-700"}`} data-testid="viva-difference">{eur(result.reconciliation.difference)}</div></div>
                  </div>
                  <div className={`mt-2 text-sm font-medium ${result.reconciliation.match ? "text-emerald-700" : "text-amber-700"}`} data-testid="viva-recon-status">
                    {result.reconciliation.match
                      ? "✓ Sluit aan: kaart + cash = omzet incl. btw."
                      : `⚠ Verschil van ${eur(result.reconciliation.difference)} — controleer de openstaande vordering (kaartclearing rond de maandgrens, refunds of ontbrekende dagontvangsten).`}
                  </div>
                </div>
              )}

              <Button onClick={() => navigate("/grootboek")} data-testid="go-grootboek" className="gap-2">
                Naar Grootboek om te bevestigen <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          )}

        </CardContent>
      </Card>

      <Card className="shadow-sm border-emerald-200">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2"><ListChecks className="h-5 w-5 text-emerald-700" /> Viva-transacties bevestigen</CardTitle>
          <p className="text-sm text-muted-foreground">Bevestig elke Viva-verrichting <b>één voor één</b>. Controleer de debet/credit-boeking, pas indien nodig de rekeningen aan, koppel kaartbetalingen aan een factuur, en klik op bevestigen.</p>
          <div className="flex items-center justify-between gap-2 rounded-md border bg-slate-50 px-3 py-2 mt-1">
            <span className="text-xs text-muted-foreground">Bestaande boekingen bijwerken naar het huidige schema: overboeking Viva→BNP via wachtrekening 58, en kosten naar 657001/657002/657003.</span>
            <Button size="sm" variant="outline" className="shrink-0" disabled={vivaBulkBusy} data-testid="viva-recompute" onClick={recomputeViva}>
              {vivaBulkBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Herbereken rekeningen (58 + 657)
            </Button>
          </div>
          <div className="flex gap-2 pt-2">
            <button className={`text-sm px-3 py-1.5 rounded-md border ${vivaTab === "pending" ? "bg-slate-800 text-white border-slate-800" : "bg-white"}`} data-testid="viva-tab-pending" onClick={() => setVivaTab("pending")}>Te bevestigen ({vivaCounts.pending_count})</button>
            <button className={`text-sm px-3 py-1.5 rounded-md border ${vivaTab === "confirmed" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white"}`} data-testid="viva-tab-confirmed" onClick={() => setVivaTab("confirmed")}>Bevestigd ({vivaCounts.confirmed_count})</button>
          </div>
          <div className="pt-2">
            <Input value={vivaSearch} onChange={(e) => setVivaSearch(e.target.value)} placeholder="Zoek op omschrijving, soort, bedrag, datum, rekening…" className="h-9" data-testid="viva-search" />
          </div>
        </CardHeader>
        <CardContent>
          {vivaFiltered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center" data-testid="viva-empty">Geen transacties. Upload eerst een Viva Account Statement.</p>
          ) : (
            <div className="space-y-2.5">
              <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/30 px-3 py-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="viva-select-all-label">
                  <input type="checkbox" className="h-4 w-4" data-testid="viva-select-all" checked={vivaAllShownSelected} onChange={toggleVivaAll} />
                  <span>Alles selecteren</span>
                </label>
                {vivaSel.size > 0 && (
                  <>
                    <span className="text-sm text-muted-foreground">{vivaSel.size} geselecteerd</span>
                    <div className="flex items-center gap-2 ml-auto">
                      {vivaTab === "pending" && (
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8" disabled={vivaBulkBusy} data-testid="viva-bulk-confirm" onClick={bulkConfirmVivaSel}>
                          {vivaBulkBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />} Bevestig geselecteerde ({vivaSel.size})
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-300 hover:bg-red-50" disabled={vivaBulkBusy} data-testid="viva-bulk-delete" onClick={bulkDeleteVivaSel}>
                        <Trash2 className="h-4 w-4 mr-1" /> Verwijder geselecteerde ({vivaSel.size})
                      </Button>
                    </div>
                  </>
                )}
              </div>
              {visibleVivaTxs.slice(0, vivaShow).map((t) => {
                const d = Math.round((t.lines || []).reduce((s, l) => s + (l.debet || 0), 0) * 100) / 100;
                const c = Math.round((t.lines || []).reduce((s, l) => s + (l.credit || 0), 0) * 100) / 100;
                return (
                  <div key={t.id} className={`rounded-lg border p-3 space-y-2 ${vivaSel.has(t.id) ? "ring-2 ring-emerald-400 bg-emerald-50/50" : ""}`} data-testid={`viva-tx-${t.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2.5 min-w-0">
                        <input type="checkbox" className="h-4 w-4 mt-0.5 shrink-0" data-testid={`viva-sel-${t.id}`} checked={vivaSel.has(t.id)} onChange={() => toggleVivaSel(t.id)} />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{t.description || "Viva-transactie"}</div>
                          <div className="text-[11px] text-muted-foreground">{t.date} · {t.category_label} · {eur(t.amount)}{t.linked_invoice_number ? ` · gekoppeld aan factuur ${t.linked_invoice_number}` : ""}</div>
                        </div>
                      </div>
                      {t.status === "confirmed"
                        ? <Badge className="bg-emerald-600 gap-1 shrink-0"><CheckCircle2 className="h-3.5 w-3.5" /> Bevestigd</Badge>
                        : <Button size="sm" disabled={vivaTxBusy === t.id || d !== c} data-testid={`viva-confirm-${t.id}`} onClick={() => confirmVivaTx(t.id)}>{vivaTxBusy === t.id ? "…" : "Bevestigen"}</Button>}
                    </div>
                    {t.status !== "confirmed" && (
                      <div className="flex items-center gap-2 flex-wrap">
                        {t.is_payment && !t.linked_invoice_number && (
                          <Button size="sm" variant="outline" className="h-8 text-xs" data-testid={`viva-link-btn-${t.id}`} disabled={vivaMatchBusy === t.id} onClick={() => loadVivaMatch(t.id)}>
                            {vivaMatchBusy === t.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Koppel aan factuur"}
                          </Button>
                        )}
                        {t.is_payment && t.linked_invoice_number && (
                          <Button size="sm" variant="ghost" className="h-8 text-xs text-red-600" data-testid={`viva-unlink-btn-${t.id}`} disabled={vivaMatchBusy === t.id} onClick={() => unlinkVivaInvoice(t.id)}>Ontkoppel factuur</Button>
                        )}
                        {!vivaLineEdit[t.id] && (
                          <Button size="sm" variant="outline" className="h-8 px-2 text-xs gap-1" data-testid={`viva-edit-${t.id}`} onClick={() => startVivaLineEdit(t)}>
                            <Pencil className="h-3.5 w-3.5" /> Rekeningen
                          </Button>
                        )}
                      </div>
                    )}
                    {vivaMatch[t.id] && (
                      <div className="rounded-md border bg-muted/30 p-2 space-y-1" data-testid={`viva-candidates-${t.id}`}>
                        <div className="text-xs text-muted-foreground px-1">Kies de bijhorende aankoopfactuur (leverancier + datum):</div>
                        {vivaMatch[t.id].candidates.length === 0 && <div className="text-xs text-muted-foreground px-1 py-1">Geen kandidaat-facturen gevonden.</div>}
                        {vivaMatch[t.id].candidates.map((c2) => (
                          <div key={c2.id} className="flex items-center justify-between gap-2 px-1 py-1 text-xs">
                            <span className="min-w-0 truncate">{c2.supplier_name} · {c2.invoice_number || "—"} · {c2.invoice_date} · {eur(c2.total_incl)} <span className="text-muted-foreground">({c2.reasons.join(", ")})</span></span>
                            <Button size="sm" variant="outline" className="h-6 text-[11px] shrink-0" data-testid={`viva-pick-${t.id}-${c2.id}`} disabled={vivaMatchBusy === t.id} onClick={() => linkVivaInvoice(t.id, c2.id)}>Koppel</Button>
                          </div>
                        ))}
                      </div>
                    )}
                    {vivaLineEdit[t.id] ? (() => {
                      const rows = vivaLineEdit[t.id];
                      const ed = Math.round(rows.reduce((s, l) => s + (parseFloat(l.debet) || 0), 0) * 100) / 100;
                      const ec = Math.round(rows.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0) * 100) / 100;
                      return (
                        <div className="rounded-md border p-2 space-y-1.5" data-testid={`viva-lines-edit-${t.id}`}>
                          <div className="grid grid-cols-[72px_1fr_78px_78px_24px] gap-1.5 text-[11px] text-muted-foreground">
                            <span>Rekening</span><span>Omschrijving</span><span className="text-right">Debet</span><span className="text-right">Credit</span><span></span>
                          </div>
                          {rows.map((l, i) => (
                            <div key={i} className="grid grid-cols-[72px_1fr_78px_78px_24px] gap-1.5 items-center">
                              <Input className="h-8 font-mono text-xs px-1.5" list="mar-accounts" placeholder="bv. 550100" data-testid={`viva-acct-${t.id}-${i}`} value={l.account} onChange={(e) => setVivaLine(t.id, i, "account", e.target.value)} />
                              <Input className="h-8 text-xs px-1.5" value={l.name} onChange={(e) => setVivaLine(t.id, i, "name", e.target.value)} />
                              <Input type="number" step="0.01" className="h-8 text-xs px-1.5 text-right" value={l.debet || ""} onChange={(e) => setVivaLine(t.id, i, "debet", e.target.value)} />
                              <Input type="number" step="0.01" className="h-8 text-xs px-1.5 text-right" value={l.credit || ""} onChange={(e) => setVivaLine(t.id, i, "credit", e.target.value)} />
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeVivaLine(t.id, i)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                            </div>
                          ))}
                          <div className="grid grid-cols-[72px_1fr_78px_78px_24px] gap-1.5 text-xs font-semibold border-t pt-1">
                            <span></span><span>Totaal</span>
                            <span className="text-right tabular-nums">{eur(ed)}</span>
                            <span className="text-right tabular-nums">{eur(ec)}</span><span></span>
                          </div>
                          <div className="flex items-center gap-2 pt-0.5">
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => addVivaLine(t.id)}>+ Regel</Button>
                            <Button size="sm" className="h-7 text-xs ml-auto" disabled={ed !== ec} data-testid={`viva-lines-save-${t.id}`} onClick={() => saveVivaLines(t.id)}>Opslaan</Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => cancelVivaLineEdit(t.id)}>Annuleren</Button>
                          </div>
                          {ed !== ec && <p className="text-[11px] text-red-600">Debet en credit moeten gelijk zijn.</p>}
                        </div>
                      );
                    })() : (
                      <div className="rounded-md bg-muted/40 p-2 space-y-0.5">
                        {(t.lines || []).map((l, i) => (
                          <div key={i} className="grid grid-cols-[70px_1fr_90px_90px] gap-2 text-xs items-center">
                            <span className="font-mono">{l.account}</span>
                            <span className="truncate">{l.name}</span>
                            <span className="text-right tabular-nums">{l.debet ? eur(l.debet) : ""}</span>
                            <span className="text-right tabular-nums">{l.credit ? eur(l.credit) : ""}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {vivaShow < visibleVivaTxs.length && (
                <Button variant="outline" className="w-full" data-testid="viva-show-more" onClick={() => setVivaShow((n) => n + 30)}>Toon meer ({visibleVivaTxs.length - vivaShow} resterend)</Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <span className="inline-flex items-center justify-center h-7 w-7 rounded-md bg-slate-700 text-white text-[10px] font-bold">BNP</span>
            BNP Paribas Fortis
          </CardTitle>
          <p className="text-sm text-muted-foreground">Rekeninguittreksel — elke transactie apart geboekt met het <b>volgnummer</b> als bewijsstuk. Leningaflossingen worden automatisch in <b>rente + kapitaal</b> gesplitst als de aflossingstabel bekend is.</p>
        </CardHeader>
        <CardContent>
          <div className="border-2 border-dashed rounded-xl p-6 text-center hover:bg-accent/40 transition-colors cursor-pointer"
            data-testid="bnp-dropzone" onClick={() => bnpRef.current?.click()}
            onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); uploadBnp(e.dataTransfer.files); }}>
            <input ref={bnpRef} type="file" accept="application/pdf" multiple className="hidden" data-testid="bnp-file-input" onChange={(e) => uploadBnp(e.target.files)} />
            {bnpBusy ? <div className="flex flex-col items-center gap-2 text-muted-foreground"><Loader2 className="h-7 w-7 animate-spin" /> Uittreksel wordt uitgelezen…</div>
              : <div className="flex flex-col items-center gap-1.5"><UploadCloud className="h-8 w-8 text-slate-700" /><div className="font-medium">Sleep je BNP-uittreksels hierheen of klik</div><div className="text-xs text-muted-foreground">Eén of meerdere PDF's tegelijk · geen extra kosten</div></div>}
          </div>
          {bnpResult && (
            <div className="mt-4 flex flex-wrap gap-3" data-testid="bnp-result">
              <Badge variant="outline" className="text-emerald-700 border-emerald-300 gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> {bnpResult.new_transactions} transacties</Badge>
              <Badge variant="outline" className="text-indigo-700 border-indigo-300 gap-1"><Percent className="h-3.5 w-3.5" /> {bnpResult.loan_splits} leningaflossingen gesplitst</Badge>
              <Badge variant="outline" className="text-amber-700 border-amber-300 gap-1"><Copy className="h-3.5 w-3.5" /> {bnpResult.duplicates_skipped} dubbels overgeslagen</Badge>
            </div>
          )}
          <div className="mt-4 flex items-center justify-between gap-2 flex-wrap rounded-md border bg-slate-50 px-3 py-2">
            <span className="text-xs text-muted-foreground">Bestaande kas↔bank-boekingen (geldstorting/‑afhaling) omzetten naar wachtrekening 58, zodat kas en bank niet dubbel tellen.</span>
            <Button size="sm" variant="outline" disabled={recBusy} data-testid="bnp-recompute-cash" onClick={recomputeCash}>
              {recBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Herbereken kas↔bank (58)
            </Button>
          </div>
          <div className="mt-2 flex items-center justify-between gap-2 flex-wrap rounded-md border bg-slate-50 px-3 py-2">
            <span className="text-xs text-muted-foreground">Bestaande (nog niet bevestigde) BNP-boekingen voor CLB, CABVS/Volckaerts en de bestuurders (Singh-Kaur) herbekennen: CLB → leverancier (440xxx) i.p.v. loon, bestuurdersloon → 455900, overige overschrijvingen → 416100.</span>
            <Button size="sm" variant="outline" disabled={recBusy} data-testid="bnp-reclassify-known" onClick={reclassifyKnown}>
              {recBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Herbeken CLB / bestuurders
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm" data-testid="bnp-confirm-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2"><ListChecks className="h-5 w-5 text-slate-700" /> BNP-transacties bevestigen</CardTitle>
          <p className="text-sm text-muted-foreground">Bevestig elke BNP-transactie <b>één voor één per volgnummer</b>. Controleer de debet/credit-boeking en de soort, en klik op bevestigen.</p>
          <div className="flex gap-2 pt-2">
            <button className={`text-sm px-3 py-1.5 rounded-md border ${bnpTab === "pending" ? "bg-slate-800 text-white border-slate-800" : "bg-white"}`} data-testid="bnp-tab-pending" onClick={() => setBnpTab("pending")}>Te bevestigen ({bnpCounts.pending_count})</button>
            <button className={`text-sm px-3 py-1.5 rounded-md border ${bnpTab === "confirmed" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white"}`} data-testid="bnp-tab-confirmed" onClick={() => setBnpTab("confirmed")}>Bevestigd ({bnpCounts.confirmed_count})</button>
          </div>
          <div className="pt-2">
            <Input value={bnpSearch} onChange={(e) => setBnpSearch(e.target.value)} placeholder="Zoek op omschrijving, volgnummer, bedrag, mededeling, rekening…" className="h-9" data-testid="bnp-search" />
          </div>
        </CardHeader>
        <CardContent>
          {bnpTab === "pending" && (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 mb-4" data-testid="bulk-afpunten">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <div className="text-sm font-semibold text-indigo-900">Bulk afpunten</div>
                  <div className="text-xs text-muted-foreground">Zoek alle transacties die exact overeenkomen met een openstaande, reeds bevestigde of een te bevestigen factuur (leverancier + bedrag/factuurnummer) en koppel ze in één keer.</div>
                </div>
                <Button size="sm" variant="outline" disabled={bulkBusy} data-testid="bulk-find" onClick={findBulk}>
                  {bulkBusy && bulkPairs === null ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ListChecks className="h-4 w-4 mr-1" />} Exacte matches zoeken
                </Button>
              </div>
              <div className="mt-2 pt-2 border-t border-indigo-100 flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs text-muted-foreground">Of koppel automatisch elke betaling waarvan het <b>factuurnummer in de mededeling</b> staat — je hoeft dan enkel nog te bevestigen.</div>
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" disabled={autoBusy} data-testid="bnp-auto-link" onClick={autoLink}>
                  {autoBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ListChecks className="h-4 w-4 mr-1" />} Automatisch koppelen (factuurnr = mededeling)
                </Button>
              </div>
              {bulkPairs !== null && (
                <div className="mt-3">
                  {bulkPairs.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Geen exacte matches gevonden.</p>
                  ) : (
                    <>
                      <div className="max-h-56 overflow-y-auto rounded border bg-white divide-y" data-testid="bulk-pairs">
                        {bulkPairs.map((p) => (
                          <div key={p.bnp_id} className="grid grid-cols-[80px_1fr_100px] gap-2 px-2 py-1.5 text-xs items-center">
                            <span className="font-mono text-amber-800">Volgnr. {p.volgnummer}</span>
                            <span className="truncate">{p.supplier_name} · factuur <b>{p.invoice_number}</b>{p.invoice_date ? ` · ${p.invoice_date}` : ""}</span>
                            <span className="text-right tabular-nums">{eur(p.amount)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center justify-end gap-2 mt-2">
                        <Button size="sm" variant="ghost" onClick={() => setBulkPairs(null)}>Annuleren</Button>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={bulkBusy} data-testid="bulk-link" onClick={linkBulk}>
                          {bulkBusy ? "Bezig…" : `Koppel alle (${bulkPairs.length})`}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          {bnpTxs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Geen transacties. Upload eerst een BNP-uittreksel.</p>
          ) : (
            <div className="space-y-2.5">
              <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/30 px-3 py-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="bnp-select-all-label">
                  <input type="checkbox" className="h-4 w-4" data-testid="bnp-select-all" checked={allShownSelected} onChange={toggleAll} />
                  <span>Alles selecteren</span>
                </label>
                {sel.size > 0 && (
                  <>
                    <span className="text-sm text-muted-foreground">{sel.size} geselecteerd</span>
                    <div className="flex items-center gap-2 ml-auto">
                      {bnpTab === "pending" && (
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8" disabled={bulkActBusy} data-testid="bnp-bulk-confirm" onClick={bulkConfirmSel}>
                          {bulkActBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />} Bevestig geselecteerde ({sel.size})
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-300 hover:bg-red-50" disabled={bulkActBusy} data-testid="bnp-bulk-delete" onClick={bulkDeleteSel}>
                        <Trash2 className="h-4 w-4 mr-1" /> Verwijder geselecteerde ({sel.size})
                      </Button>
                    </div>
                  </>
                )}
              </div>
              {visibleTxs.slice(0, bnpShow).map((t) => {
                const d = Math.round(t.lines.reduce((s, l) => s + l.debet, 0) * 100) / 100;
                const c = Math.round(t.lines.reduce((s, l) => s + l.credit, 0) * 100) / 100;
                return (
                  <div key={t.id} className={`rounded-lg border p-3 space-y-2 ${sel.has(t.id) ? "ring-2 ring-slate-400 bg-slate-50/60" : ""}`} data-testid={`bnp-tx-${t.volgnummer}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2.5 min-w-0">
                        <input type="checkbox" className="h-4 w-4 mt-0.5 shrink-0" data-testid={`bnp-sel-${t.volgnummer}`} checked={sel.has(t.id)} onChange={() => toggleSel(t.id)} />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{t.description || "BNP-transactie"}</div>
                          <div className="text-[11px] text-muted-foreground">Volgnr. <b>{t.volgnummer}</b> · {t.date} · {eur(t.amount)}{t.mededeling ? ` · ${t.mededeling}` : ""}{t.linked_invoice_number ? ` · gekoppeld aan factuur ${t.linked_invoice_number}` : ""}</div>
                        </div>
                      </div>
                      {t.status === "confirmed"
                        ? <Badge className="bg-emerald-600 gap-1 shrink-0"><CheckCircle2 className="h-3.5 w-3.5" /> Bevestigd</Badge>
                        : <Button size="sm" disabled={txBusy === t.id || d !== c} data-testid={`bnp-confirm-${t.volgnummer}`} onClick={() => confirmTx(t.id)}>{txBusy === t.id ? "…" : "Bevestigen"}</Button>}
                    </div>
                    {t.is_payment && (
                      <div className="flex items-center gap-2 flex-wrap">
                        {!t.linked_invoice_number && (
                          <Button size="sm" variant="outline" className="h-8 text-xs" data-testid={`bnp-link-btn-${t.volgnummer}`} disabled={bnpMatchBusy === t.id} onClick={() => loadBnpMatch(t.id)}>
                            {bnpMatchBusy === t.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Koppel aan factuur"}
                          </Button>
                        )}
                        {t.linked_invoice_number && (
                          <Button size="sm" variant="ghost" className="h-8 text-xs text-red-600" data-testid={`bnp-unlink-btn-${t.volgnummer}`} disabled={bnpMatchBusy === t.id} onClick={() => unlinkBnpInvoice(t.id)}>Ontkoppel factuur</Button>
                        )}
                      </div>
                    )}
                    {bnpMatch[t.id] && (
                      <div className="rounded-md border bg-muted/30 p-2 space-y-1" data-testid={`bnp-candidates-${t.volgnummer}`}>
                        <div className="text-xs text-muted-foreground px-1">Kies de bijhorende aankoopfactuur (leverancier · datum · factuurnr · bedrag):</div>
                        {bnpMatch[t.id].candidates.length === 0 && <div className="text-xs text-muted-foreground px-1 py-1">Geen kandidaat-facturen gevonden (betaaldatum mag niet vóór de factuurdatum liggen).</div>}
                        {bnpMatch[t.id].candidates.map((c2) => (
                          <div key={c2.id} className="flex items-center justify-between gap-2 px-1 py-1 text-xs">
                            <span className="min-w-0 truncate">{c2.supplier_name} · {c2.invoice_number || "—"} · {c2.invoice_date} · {eur(c2.total_incl)} <span className="text-muted-foreground">({c2.reasons.join(", ")})</span></span>
                            <Button size="sm" variant="outline" className="h-6 text-[11px] shrink-0" data-testid={`bnp-pick-${t.volgnummer}-${c2.id}`} disabled={bnpMatchBusy === t.id} onClick={() => linkBnpInvoice(t.id, c2.id)}>Koppel</Button>
                          </div>
                        ))}
                      </div>
                    )}
                    {t.status !== "confirmed" && !t.linked_invoice_number && (
                      <div className="flex items-center gap-2">
                        <select className="flex-1 text-sm border rounded-md h-8 px-2 bg-white" data-testid={`bnp-cat-${t.volgnummer}`}
                          value={t.category} onChange={(e) => setCategory(t.id, e.target.value)} disabled={!!bnpLineEdit[t.id]}>
                          {bnpCats.map((c2) => <option key={c2.key} value={c2.key}>{c2.label}</option>)}
                        </select>
                        {!bnpLineEdit[t.id] && (
                          <Button size="sm" variant="outline" className="h-8 px-2 text-xs gap-1" data-testid={`bnp-edit-${t.volgnummer}`} onClick={() => startLineEdit(t)}>
                            <Pencil className="h-3.5 w-3.5" /> Rekeningen
                          </Button>
                        )}
                      </div>
                    )}
                    {bnpLineEdit[t.id] ? (() => {
                      const rows = bnpLineEdit[t.id];
                      const ed = Math.round(rows.reduce((s, l) => s + (parseFloat(l.debet) || 0), 0) * 100) / 100;
                      const ec = Math.round(rows.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0) * 100) / 100;
                      return (
                        <div className="rounded-md border p-2 space-y-1.5" data-testid={`bnp-lines-edit-${t.volgnummer}`}>
                          <div className="grid grid-cols-[72px_1fr_78px_78px_24px] gap-1.5 text-[11px] text-muted-foreground">
                            <span>Rekening</span><span>Omschrijving</span><span className="text-right">Debet</span><span className="text-right">Credit</span><span></span>
                          </div>
                          {rows.map((l, i) => (
                            <div key={i} className="grid grid-cols-[72px_1fr_78px_78px_24px] gap-1.5 items-center">
                              <Input className="h-8 font-mono text-xs px-1.5" list="mar-accounts" placeholder="bv. 604006" data-testid={`bnp-acct-${t.volgnummer}-${i}`} value={l.account} onChange={(e) => setLine(t.id, i, "account", e.target.value)} />
                              <Input className="h-8 text-xs px-1.5" value={l.name} onChange={(e) => setLine(t.id, i, "name", e.target.value)} />
                              <Input type="number" step="0.01" className="h-8 text-xs px-1.5 text-right" value={l.debet || ""} onChange={(e) => setLine(t.id, i, "debet", e.target.value)} />
                              <Input type="number" step="0.01" className="h-8 text-xs px-1.5 text-right" value={l.credit || ""} onChange={(e) => setLine(t.id, i, "credit", e.target.value)} />
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeLine(t.id, i)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                            </div>
                          ))}
                          <div className="grid grid-cols-[72px_1fr_78px_78px_24px] gap-1.5 text-xs font-semibold border-t pt-1">
                            <span></span><span>Totaal</span>
                            <span className="text-right tabular-nums">{eur(ed)}</span>
                            <span className="text-right tabular-nums">{eur(ec)}</span><span></span>
                          </div>
                          <div className="flex items-center gap-2 pt-0.5">
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => addLine(t.id)}>+ Regel</Button>
                            <Button size="sm" className="h-7 text-xs ml-auto" disabled={ed !== ec} data-testid={`bnp-lines-save-${t.volgnummer}`} onClick={() => saveLines(t.id)}>Opslaan</Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => cancelLineEdit(t.id)}>Annuleren</Button>
                          </div>
                          {ed !== ec && <p className="text-[11px] text-red-600">Debet en credit moeten gelijk zijn.</p>}
                        </div>
                      );
                    })() : (
                      <div className="rounded-md bg-muted/40 p-2 space-y-0.5">
                        {t.lines.map((l, i) => (
                          <div key={i} className="grid grid-cols-[70px_1fr_90px_90px] gap-2 text-xs items-center">
                            <span className="font-mono">{l.account}</span>
                            <span className="truncate">{l.name}</span>
                            <span className="text-right tabular-nums">{l.debet ? eur(l.debet) : ""}</span>
                            <span className="text-right tabular-nums">{l.credit ? eur(l.credit) : ""}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {bnpShow < visibleTxs.length && (
                <Button variant="outline" className="w-full" data-testid="bnp-show-more" onClick={() => setBnpShow((n) => n + 30)}>Toon meer ({visibleTxs.length - bnpShow} resterend)</Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-primary" /> Aflossingstabel (lening)</CardTitle>
          <p className="text-sm text-muted-foreground">Upload de aflossingstabel van je krediet. Het systeem <b>herkent zelf</b> dat het een aflossingstabel is en gebruikt kapitaal/rente per vervaldag om BNP-aflossingen automatisch te splitsen.</p>
        </CardHeader>
        <CardContent>
          <div className="border-2 border-dashed rounded-xl p-6 text-center hover:bg-accent/40 transition-colors cursor-pointer"
            data-testid="loan-dropzone" onClick={() => loanRef.current?.click()}
            onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); uploadLoan(e.dataTransfer.files?.[0]); }}>
            <input ref={loanRef} type="file" accept="application/pdf" className="hidden" data-testid="loan-file-input" onChange={(e) => uploadLoan(e.target.files?.[0])} />
            {loanBusy ? <div className="flex flex-col items-center gap-2 text-muted-foreground"><Loader2 className="h-7 w-7 animate-spin" /> Tabel wordt uitgelezen…</div>
              : <div className="flex flex-col items-center gap-1.5"><UploadCloud className="h-8 w-8 text-primary" /><div className="font-medium">Sleep de aflossingstabel hierheen of klik</div><div className="text-xs text-muted-foreground">PDF · wordt automatisch herkend</div></div>}
          </div>
          {loanResult && (
            <div className="mt-4 rounded-lg border p-3 bg-emerald-50/50 text-sm" data-testid="loan-result">
              <div className="font-medium text-emerald-800 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> {loanResult.is_update ? "Aflossingstabel bijgewerkt" : "Aflossingstabel toegevoegd"}</div>
              <div className="text-muted-foreground mt-1">Contract <b>{loanResult.contract}</b> · {loanResult.aantal} aflossingen · maandbedrag {eur(loanResult.maandbedrag)} · totaal kapitaal {eur(loanResult.kapitaal_totaal)}{loanResult.contract_detected ? "" : " · ⚠ contractnummer niet automatisch herkend"}</div>
            </div>
          )}
          {loans.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="loans-list">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Geregistreerde leningen ({loans.length}) — kost & aflossing per lening</div>
              {loans.map((l) => {
                const years = Object.keys(l.kapitaal_per_jaar || {}).sort();
                return (
                  <div key={l.id} className="rounded-lg border p-3" data-testid={`loan-item-${l.contract}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium">Contract {l.contract}</div>
                        <div className="text-[11px] text-muted-foreground">{l.aantal} aflossingen · {l.eerste_vervaldag} → {l.laatste_vervaldag} · maandbedrag {eur(l.maandbedrag)}</div>
                      </div>
                      <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" data-testid={`loan-delete-${l.contract}`} onClick={() => deleteLoan(l.id, l.contract)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2 text-xs">
                      <div><div className="text-muted-foreground">Totaal kapitaal</div><div className="font-semibold tabular-nums">{eur(l.kapitaal_totaal)}</div></div>
                      <div><div className="text-muted-foreground">Totale rente (kost)</div><div className="font-semibold tabular-nums text-amber-700">{eur(l.rente_totaal)}</div></div>
                      <div><div className="text-muted-foreground">Vervaldagen</div><div className="font-semibold tabular-nums">{l.aantal}</div></div>
                    </div>
                    {years.length > 0 && (
                      <div className="mt-2 border-t pt-2">
                        <div className="text-[11px] text-muted-foreground mb-1">Kapitaal dat per jaar vervalt (voor herklassering lang → kort, 173000 → 423000 per 31/12 vorig jaar):</div>
                        <div className="flex flex-wrap gap-1.5">
                          {years.map((y) => (
                            <Badge key={y} variant="outline" className="text-[11px] gap-1" data-testid={`loan-year-${l.contract}-${y}`}>{y}: {eur(l.kapitaal_per_jaar[y])}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              <p className="text-[11px] text-muted-foreground">De eindejaarsboeking (herklassering langlopend → kortlopend) verschijnt automatisch in het Grootboek op 31/12 van elk jaar, per lening, op basis van deze tabellen.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
